import { Stack, Title, Text, Group, Button, Paper, Checkbox, SimpleGrid, ThemeIcon, Chip, Tooltip, ActionIcon } from '@mantine/core';
import { IconSalad, IconArrowRight, IconArrowLeft, IconInfoCircle } from '@tabler/icons-react';
import { useState, useEffect } from 'react';
import { FoodAutocompleteInput } from '../UI/FoodAutocompleteInput';
import { searchIngredients } from '../../data/ingredientDatabase';
import { HouseholdSegment, bySegment } from '../../utils/householdSegment';
import {
  DIETARY_RESTRICTIONS,
  NUTRITION_GOALS,
  NUTRITION_GOAL_PRESETS,
  COMMON_ALLERGIES,
  RESTRICTION_TOOLTIPS,
  hasExclusionConflict,
  partitionDietarySelections,
  isGoalPresetActive,
  toggleGoalPreset,
} from '../../utils/dietaryOptions';
import { useDietarySelection } from '../../hooks/useDietarySelection';

type DietaryRestrictionsStepProps = {
  value: { dietaryRestrictions: string[]; allergies: string[] };
  onChange: (value: { dietaryRestrictions: string[]; allergies: string[] }) => void;
  onNext: () => void;
  onBack: () => void;
  isLastStep: boolean;
  isCompleting?: boolean;
  householdSegment?: HouseholdSegment;
};

export const DietaryRestrictionsStep = ({ value, onChange, onNext, onBack, isLastStep, householdSegment }: DietaryRestrictionsStepProps) => {
  const { restrictions: selectedRestrictions, goals: selectedGoals } = partitionDietarySelections(value.dietaryRestrictions);

  const [allergyInput, setAllergyInput] = useState('');
  const [showDietaryOptions, setShowDietaryOptions] = useState(selectedRestrictions.length > 0);
  const [showGoalOptions, setShowGoalOptions] = useState(selectedGoals.length > 0);
  const [showAllergyOptions, setShowAllergyOptions] = useState(value.allergies.length > 0);
  const [noDietaryRestrictions, setNoDietaryRestrictions] = useState(false);
  const [noNutritionGoals, setNoNutritionGoals] = useState(false);
  const [noAllergies, setNoAllergies] = useState(false);
  const [pendingNext, setPendingNext] = useState(false);

  // Effect to call onNext after state has been updated
  useEffect(() => {
    if (pendingNext) {
      setPendingNext(false);
      onNext();
    }
  }, [value.allergies, pendingNext, onNext]);

  const {
    toggleAllergy,
    addAllergiesFromText,
    removeAllergy,
    clearAllergies,
    toggleDietaryRestriction,
    setDietaryRestrictions,
  } = useDietarySelection(value, onChange);

  // Clear only one section's selections, preserving the other section and
  // hidden allergy-synced tags (Nut-Free, Soy-Free, Egg-Free).
  const clearSection = (sectionOptions: string[]) => {
    setDietaryRestrictions(
      value.dietaryRestrictions.filter(r => !sectionOptions.includes(r))
    );
  };

  const addAllergy = (text: string) => {
    if (addAllergiesFromText(text)) {
      setAllergyInput('');
    }
  };

  const handleNext = () => {
    // Auto-add any text in the allergy input before proceeding
    if (allergyInput.trim() && addAllergiesFromText(allergyInput)) {
      setAllergyInput('');
      setPendingNext(true);
      return;
    }

    onNext();
  };

  // Separate predefined and custom allergies for display
  const predefinedAllergies = value.allergies.filter(a => COMMON_ALLERGIES.includes(a));
  const customAllergies = value.allergies.filter(a => !COMMON_ALLERGIES.includes(a));

  // Ingredient-catalog suggestions for the "Other allergies" input (same catalog
  // that powers pantry autocomplete). Free text is always allowed since many
  // allergens (e.g. sulfites) aren't ingredients. Search once there are 2+
  // characters, and hide anything already selected.
  const allergySuggestions = allergyInput.trim().length >= 2
    ? searchIngredients(allergyInput, 8)
        .map(ing => ing.displayName ?? ing.name)
        .filter(name => !value.allergies.some(a => a.toLowerCase() === name.toLowerCase()))
    : [];

  // Conflict dimming evaluates against the COMBINED selection because
  // exclusion groups span sections (Keto vs the fat goals, High-Fiber vs
  // Low-Residue).
  const renderOptionGrid = (options: string[]) => (
    <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
      {options.map((option) => {
        const isSelected = value.dietaryRestrictions.includes(option);
        const hasConflict = hasExclusionConflict(value.dietaryRestrictions, option);
        const tooltip = RESTRICTION_TOOLTIPS[option];

        const checkbox = (
          <Checkbox
            key={tooltip ? undefined : option}
            label={option}
            checked={isSelected}
            onChange={() => toggleDietaryRestriction(option)}
            size="md"
            color="orange"
            style={{ opacity: hasConflict ? 0.5 : 1 }}
            styles={{
              label: { fontSize: '14px' },
              root: { cursor: 'pointer' },
              input: { cursor: 'pointer' }
            }}
          />
        );

        if (!tooltip) {
          return checkbox;
        }

        return (
          <Group key={option} gap="xs" wrap="nowrap">
            {checkbox}
            <Tooltip
              label={tooltip}
              multiline
              w={220}
              withArrow
              events={{ hover: true, focus: true, touch: true }}
            >
              <ActionIcon size="xs" variant="subtle" color="gray">
                <IconInfoCircle size={14} />
              </ActionIcon>
            </Tooltip>
          </Group>
        );
      })}
    </SimpleGrid>
  );

  // Quick-pick chips with derived state: a chip is on iff every goal in its
  // bundle is checked below — the checkboxes stay the source of truth.
  // Toggling on completes the bundle; toggling off unchecks it.
  const renderGoalPresets = () => (
    <Group gap="xs" wrap="wrap" align="center">
      {NUTRITION_GOAL_PRESETS.map((preset) => (
        <Chip
          key={preset.key}
          checked={isGoalPresetActive(value.dietaryRestrictions, preset.goals)}
          onChange={() =>
            setDietaryRestrictions(
              toggleGoalPreset(value.dietaryRestrictions, preset.goals)
            )
          }
          color="orange"
          size="sm"
        >
          {preset.label}
        </Chip>
      ))}
    </Group>
  );

  const summaryLines: string[] = [];
  if (selectedRestrictions.length > 0) {
    summaryLines.push(`Restrictions: ${selectedRestrictions.join(', ')}`);
  } else if (noDietaryRestrictions) {
    summaryLines.push('No dietary restrictions');
  }
  if (selectedGoals.length > 0) {
    summaryLines.push(`Nutrition goals: ${selectedGoals.join(', ')}`);
  } else if (noNutritionGoals) {
    summaryLines.push('No nutrition goals');
  }
  if (value.allergies.length > 0) {
    summaryLines.push(`Allergies: ${value.allergies.join(', ')}`);
  } else if (noAllergies) {
    summaryLines.push('No food allergies');
  }

  return (
    <Stack gap="xl" w={{ base: '100%', sm: 700 }} mx="auto" px={{ base: 'sm', sm: 'md' }}>
      {/* Header */}
      <Stack gap="md" ta="center" align="center">
        <ThemeIcon size={80} radius="xl" variant="gradient" gradient={{ from: 'orange', to: 'red' }}>
          <IconSalad size={40} />
        </ThemeIcon>
        <Stack gap="xs" align="center">
          <Title order={2} size="h1" fw={700} c="dark">
            Dietary preferences
          </Title>
          <Text size="lg" c="dimmed" ta="center" maw={400}>
            {bySegment(householdSegment, {
              family: 'Help us suggest meals that work well for your family',
              household: 'Help us suggest meals that work well for your household',
              solo: 'Help us suggest meals that work well for you',
            })}
          </Text>
        </Stack>
      </Stack>

      {/* Dietary Preferences */}
        <Paper
          radius="lg"
          p={{ base: 'md', sm: 'xl' }}
          style={{
            border: '1px solid var(--mantine-color-gray-2)',
            width: '100%'
          }}
        >
          <Stack gap="xl">
            {/* Dietary Restrictions */}
            <Stack gap="md">
              <Text fw={600} size="lg">
                Any dietary restrictions?
              </Text>

              {!showDietaryOptions && !noDietaryRestrictions ? (
                <Stack gap="sm">
                  <Group gap="sm">
                    <Button
                      variant="subtle"
                      color="gray"
                      size="sm"
                      onClick={() => setShowDietaryOptions(true)}
                    >
                      Yes
                    </Button>
                    <Button
                      variant="subtle"
                      color="gray"
                      size="sm"
                      onClick={() => {
                        setNoDietaryRestrictions(true);
                        clearSection(DIETARY_RESTRICTIONS);
                      }}
                    >
                      No
                    </Button>
                  </Group>
                </Stack>
              ) : showDietaryOptions ? (
                <Stack gap="md">
                  <Group justify="space-between" align="center">
                    <Text size="sm" c="dimmed">
                      {bySegment(householdSegment, {
                        family: 'Select any that apply to your family',
                        household: 'Select any that apply to your household',
                        solo: 'Select any that apply to you',
                      })}
                    </Text>
                    <Button
                      variant="subtle"
                      size="xs"
                      color="gray"
                      onClick={() => {
                        setShowDietaryOptions(false);
                        setNoDietaryRestrictions(false);
                        clearSection(DIETARY_RESTRICTIONS);
                      }}
                    >
                      Cancel
                    </Button>
                  </Group>

                  {renderOptionGrid(DIETARY_RESTRICTIONS)}
                </Stack>
              ) : (
                <Stack gap="sm">
                  <Text size="sm" c="green.7">✓ No dietary restrictions</Text>
                  <Button
                    variant="subtle"
                    size="xs"
                    color="gray"
                    onClick={() => {
                      setNoDietaryRestrictions(false);
                    }}
                    style={{ alignSelf: 'flex-start' }}
                  >
                    Change
                  </Button>
                </Stack>
              )}
            </Stack>

            {/* Nutrition Goals */}
            <Stack gap="md">
              <Text fw={600} size="lg">
                Any nutrition goals?
              </Text>

              {!showGoalOptions && !noNutritionGoals ? (
                <Stack gap="sm">
                  <Group gap="sm">
                    <Button
                      variant="subtle"
                      color="gray"
                      size="sm"
                      onClick={() => setShowGoalOptions(true)}
                    >
                      Yes
                    </Button>
                    <Button
                      variant="subtle"
                      color="gray"
                      size="sm"
                      onClick={() => {
                        setNoNutritionGoals(true);
                        clearSection(NUTRITION_GOALS);
                      }}
                    >
                      No
                    </Button>
                  </Group>
                </Stack>
              ) : showGoalOptions ? (
                <Stack gap="md">
                  <Group justify="space-between" align="center">
                    <Text size="sm" c="dimmed">
                      Every planned meal will meet the targets you pick
                    </Text>
                    <Button
                      variant="subtle"
                      size="xs"
                      color="gray"
                      onClick={() => {
                        setShowGoalOptions(false);
                        setNoNutritionGoals(false);
                        clearSection(NUTRITION_GOALS);
                      }}
                    >
                      Cancel
                    </Button>
                  </Group>

                  {renderGoalPresets()}

                  {renderOptionGrid(NUTRITION_GOALS)}
                </Stack>
              ) : (
                <Stack gap="sm">
                  <Text size="sm" c="green.7">✓ No nutrition goals</Text>
                  <Button
                    variant="subtle"
                    size="xs"
                    color="gray"
                    onClick={() => {
                      setNoNutritionGoals(false);
                    }}
                    style={{ alignSelf: 'flex-start' }}
                  >
                    Change
                  </Button>
                </Stack>
              )}
            </Stack>

            {/* Allergies */}
            <Stack gap="md">
              <Text fw={600} size="lg">
                Any food allergies?
              </Text>

              {!showAllergyOptions && !noAllergies ? (
                <Stack gap="sm">
                  <Group gap="sm">
                    <Button
                      variant="subtle"
                      color="gray"
                      size="sm"
                      onClick={() => setShowAllergyOptions(true)}
                    >
                      Yes
                    </Button>
                    <Button
                      variant="subtle"
                      color="gray"
                      size="sm"
                      onClick={() => {
                        setNoAllergies(true);
                        clearAllergies();
                        setAllergyInput('');
                      }}
                    >
                      No
                    </Button>
                  </Group>
                </Stack>
              ) : showAllergyOptions ? (
                <Stack gap="md">
                  <Group justify="space-between" align="center">
                    <Text size="sm" c="dimmed">We'll make sure to avoid these ingredients</Text>
                    <Button
                      variant="subtle"
                      size="xs"
                      color="gray"
                      onClick={() => {
                        setShowAllergyOptions(false);
                        setNoAllergies(false);
                        clearAllergies();
                        setAllergyInput('');
                      }}
                    >
                      Cancel
                    </Button>
                  </Group>

                  <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                    {COMMON_ALLERGIES.map((allergy) => (
                      <Checkbox
                        key={allergy}
                        label={allergy}
                        checked={predefinedAllergies.includes(allergy)}
                        onChange={() => toggleAllergy(allergy)}
                        size="md"
                        color="red"
                        styles={{
                          label: { fontSize: '14px' }
                        }}
                      />
                    ))}
                  </SimpleGrid>

                  {/* Other allergies input */}
                  <Stack gap="xs">
                    <Text fw={500} size="sm">Other allergies</Text>
                    <FoodAutocompleteInput
                      value={allergyInput}
                      onChange={setAllergyInput}
                      onAdd={addAllergy}
                      suggestions={allergySuggestions}
                      placeholder="e.g. sulfites, celery, corn..."
                      color="red"
                    />

                    {/* Custom allergies display */}
                    {customAllergies.length > 0 && (
                      <Group gap="xs" wrap="wrap">
                        {customAllergies.map(allergy => (
                          <Chip
                            key={allergy}
                            checked={true}
                            onChange={() => removeAllergy(allergy)}
                            variant="filled"
                            color="red"
                            size="sm"
                            style={{ minWidth: 'fit-content' }}
                          >
                            {allergy}
                          </Chip>
                        ))}
                      </Group>
                    )}
                  </Stack>
                </Stack>
              ) : (
                <Stack gap="sm">
                  <Text size="sm" c="green.7">✓ No food allergies</Text>
                  <Button
                    variant="subtle"
                    size="xs"
                    color="gray"
                    onClick={() => {
                      setNoAllergies(false);
                    }}
                    style={{ alignSelf: 'flex-start' }}
                  >
                    Change
                  </Button>
                </Stack>
              )}
            </Stack>
          </Stack>
        </Paper>

      {/* Summary */}
      {summaryLines.length > 0 && (
        <Paper
          p="lg"
          radius="lg"
          bg="gray.0"
          style={{
            border: '1px solid var(--mantine-color-gray-2)',
            minHeight: 80,
            width: '100%'
          }}
        >
          <Group gap="md" align="center" style={{ height: '100%' }}>
            <ThemeIcon
              size={40}
              radius="md"
              color="green"
              variant="light"
            >
              <IconSalad size={20} />
            </ThemeIcon>

            <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
              <Text fw={600} c="green.7" style={{ wordBreak: 'break-word' }}>
                Preferences saved!
              </Text>
              <Text size="sm" c="dimmed" style={{ wordBreak: 'break-word' }}>
                {summaryLines.map((line, index) => (
                  <span key={line}>
                    {index > 0 && <br />}
                    {line}
                  </span>
                ))}
              </Text>
            </Stack>
          </Group>
        </Paper>
      )}

      {/* Navigation */}
      <Group justify="space-between" pt="md" wrap="nowrap" className="wizard-footer">
        <Button
          variant="subtle"
          size="md"
          leftSection={<IconArrowLeft size={18} />}
          onClick={onBack}
          color="orange"
        >
          Back
        </Button>

        <Button
          size="md"
          rightSection={<IconArrowRight size={18} />}
          onClick={handleNext}
          color="orange"
          variant="filled"
        >
          {isLastStep ? 'Finish' : 'Continue'}
        </Button>
      </Group>
    </Stack>
  );
};
